import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Home, BookOpen, User, ArrowRight, CheckCircle2 } from 'lucide-react';

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>About | MyWeightForecast.com</title>
        <meta name="description" content="Why I built MyWeightForecast.com — a simple, AI-powered tool to help you understand what your weight loss journey could realistically look like." />
        <meta property="og:title" content="About | MyWeightForecast.com" />
        <meta property="og:description" content="Why I built MyWeightForecast.com — clarity over hype for your weight loss journey." />
      </Helmet>

      <div className="min-h-screen bg-[#020817] text-white">
        {/* Navigation Header */}
        <header className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/80 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 flex items-center justify-center group-hover:border-cyan-500/50 transition-all">
                <span className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">B</span>
              </div>
              <span className="font-heading text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">Bryan 2.0</span>
            </Link>
            
            <nav className="flex items-center gap-6">
              <Link to="/" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Home</span>
              </Link>
              <Link to="/blog" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                <span className="hidden sm:inline">Blog</span>
              </Link>
              <Link to="/about" className="text-cyan-400 font-medium flex items-center gap-2">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">About</span>
              </Link>
              <Button
                onClick={() => navigate('/quiz')}
                className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-6 py-2 text-sm font-bold shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] transition-all"
                data-testid="nav-cta-btn"
              >
                Get My Forecast
              </Button>
            </nav>
          </div>
        </header>

        {/* Background glow effect */}
        <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#22D3EE] opacity-5 blur-[150px] rounded-full pointer-events-none"></div>

        {/* Main Content */}
        <main className="relative z-10 py-16 sm:py-24 px-4 sm:px-6">
          <div className="max-w-[680px] mx-auto">
            
            {/* Why I Built This */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-cyan-500/20 bg-cyan-500/5">
                <User className="h-4 w-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-medium">THE STORY</span>
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-8 leading-tight">
                Why I <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">Built This</span>
              </h1>
              
              <div className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-2xl p-8 sm:p-10 mb-12">
                <div className="space-y-6 text-lg text-slate-300 leading-relaxed">
                  <p>I kept seeing the same thing over and over.</p>
                  
                  <p>
                    People were interested in medical weight loss — semaglutide, tirzepatide, all of it — but they didn't really know what to expect. The information online is scattered. Pricing varies wildly. Timelines vary. And most people are just guessing.
                  </p>
                  
                  <p>
                    I've spent years in sales and working closely in this space, talking to real people every day who are trying to lose weight, feel better, and figure out what actually works.
                  </p>
                  
                  <p className="text-white font-medium">
                    What I kept noticing: most people don't need more hype. They need clarity.
                  </p>
                  
                  <p>
                    That's why I built <span className="text-cyan-400 font-medium">MyWeightForecast.com</span>.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* What This Tool Actually Does */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-12"
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-6">
                What This Tool <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">Actually Does</span>
              </h2>
              
              <div className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-2xl p-8 sm:p-10">
                <div className="space-y-6 text-lg text-slate-300 leading-relaxed">
                  <p>
                    This is a simple, AI-powered forecasting tool that gives you a personalized look at what your weight loss journey could realistically look like — based on your starting point, your goals, and the medication you're considering.
                  </p>
                  
                  <p className="text-white font-medium">
                    Not promises. Not guarantees. Just a clearer picture so you can make a more informed decision.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Who This Is For */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-12"
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-6">
                Who This Is <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">For</span>
              </h2>
              
              <div className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-2xl p-8 sm:p-10">
                <ul className="space-y-4">
                  <li className="flex items-start gap-4">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-slate-300">People exploring GLP-1 options like semaglutide or tirzepatide</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-slate-300">People who want to understand realistic timelines, not just highlight reels</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-slate-300">People who are serious about making a change but want to go in with eyes open</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Closing + CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="backdrop-blur-xl bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border border-emerald-500/20 rounded-2xl p-8 sm:p-10 text-center">
                <p className="text-lg text-slate-300 mb-2">I'm not here to push you into anything.</p>
                <p className="text-xl text-white font-medium mb-8">
                  I'm here to help you understand your options — so if you do move forward, you feel confident doing it.
                </p>
                
                <Button
                  onClick={() => navigate('/quiz')}
                  className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-10 py-6 text-lg font-bold shadow-[0_0_30px_rgba(16,185,129,0.4)] hover:shadow-[0_0_40px_rgba(16,185,129,0.6)] transition-all hover:scale-105"
                  data-testid="about-cta-btn"
                >
                  Take the Free Forecast Quiz
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>

          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-slate-800/50 py-8 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <p>&copy; {new Date().getFullYear()} Bryan 2.0 Forecast. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link to="/" className="hover:text-cyan-400 transition-colors">Home</Link>
              <Link to="/blog" className="hover:text-cyan-400 transition-colors">Blog</Link>
              <Link to="/about" className="hover:text-cyan-400 transition-colors">About</Link>
              <Link to="/quiz" className="hover:text-cyan-400 transition-colors">Take Quiz</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AboutPage;
